import {Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {AlertListService} from '@xref/ngx';
import {ElementHelperService} from '../../services/element-helper/element-helper.service';
import {TokenService} from '../../services/token/token.service';
import {NgForm} from '@angular/forms';
import {IdService} from "../../services/id/id.service";
import {PermissionsService} from "src/app/services/permissions/permissions.service";
@Component({
    selector: 'xlog-reset-password',
    templateUrl: './reset-password.component.pug',
    styleUrls: ['./reset-password.component.scss'],
    standalone: false
})
export class ResetPasswordComponent implements OnInit {
    @ViewChild('formRef', {static: true}) form: NgForm;

    loaded = false;
    passwordPolicyLoaded = false;
    inputType = 'password';
    platform: 'omni' | 'classic' = 'classic';

    passwordPolicy = {
        complex_password_rules: {
            force_lower_case: <boolean> null,
            force_numbers: <boolean> null,
            force_special_chars: <boolean> null,
            force_upper_case: <boolean> null
        },
        inactive_account_suspension_trigger: <number> null,
        password_changes_per_day: <number> null,
        password_expiry: <number> null,
        previous_passwords_to_remember: <number> null,
        session_age: <number> null,
        password_minimum_length: <number> null
    };

    model = {
        password: ''
    };
    ent_9105_penetration_test_2025 = false;
    ent_10313_password_policy_and_mfa_global = false;

    constructor(
        private id: IdService,
        private alertList: AlertListService,
        private router: Router,
        private route: ActivatedRoute,
        private token: TokenService,
        private el: ElementHelperService,
        private featureFlagService: PermissionsService
    ) {}

    ngOnInit() {
        this.platform = this.route.snapshot.queryParams['p'] === 'o' ? 'omni' : 'classic';

        if (this.platform === 'classic') {
            this.id.validateResetPasswordToken(
                this.token.data.token, 'xrp', this.getResetPasswordPolicySuccess, this.validationError
            );
        } else {
            this.id.validateResetPasswordToken(
                this.token.data.token, undefined, this.validationSuccess, this.validationError
            );
        }
        this.ent_9105_penetration_test_2025 = this.featureFlagService.isFeatureActivated('ENT-9105-PENETRATION-TEST-2025');
        this.ent_10313_password_policy_and_mfa_global = this.featureFlagService.isFeatureActivated(
            'ent-10313-password-policy-and-mfa-global'
        );
    }

    resetPassword() {
        this.loaded = false;
        this.id.resetPassword(
            {
                password: this.model.password,
                token: this.token.data.token
            },
            this.resetPasswordSuccess, this.resetPasswordError
        );
    }

    toggleShowPassword() {
        this.inputType = this.inputType === 'text' ? 'password' : 'text';
    }

    sanitizePassword(value: string) {
        this.model.password = value;

        if (value !== null) {
            setTimeout(() => {
                this.model.password = value.replace(/\s/g, '');
            });
        }
    }

    private getResetPasswordPolicySuccess = (data: any) => {
        if (data.message === 'User inactive.') {
            this.alertList.append([{ active: true, type: 'danger', messageKey: 'alerts.auth.XREF_USER_INACTIVE', timeout: 0, keepOnRouteChange: true }]);
            this.router.navigate(['/login']);

        } else {
            this.loaded = true;
            this.passwordPolicyLoaded = true;
            this.passwordPolicy = data.data;
            this.el.focusById('newPassword');
        }
    };

    private validationError = (error: any) => {
        switch (error.status_message) {
            case 'connectivity_error':
                this.alertList.append([{active: true, type: 'danger', messageKey: 'alerts.connectivityError', timeout: 0, keepOnRouteChange: true}]);
                break;
            case 'redirect':
                this.alertList.append([{active: true, type: 'danger', messageKey: `alerts.auth.${error.data.xref_error}`, keepOnRouteChange: true}]);
                break;
            case 'client_error':
                this.alertList.append([{active: true, type: 'danger', messageKey: `alerts.password.${error.message}`, keepOnRouteChange: true}]);
                break;
            default:
                this.alertList.append([{active: true, type: 'danger', messageKey: 'alerts.generic.error', keepOnRouteChange: true}]);
                break;
        }

        this.loaded = true;
        this.router.navigate(['/login']);
    };

    private resetPasswordSuccess = () => {
        this.alertList.clear(true);
        this.alertList.append([{active: true, type: 'success', messageKey: 'alerts.password.passwordResetSuccess', keepOnRouteChange: true}]);

        this.loaded = true;
        this.router.navigate(['/login']);
    };

    private resetPasswordError = (error: any) => {
        let messageKey = 'alerts.generic.error';
        let passwordsToRemember = this.passwordPolicy ? this.passwordPolicy.previous_passwords_to_remember : 0;
        const reuseMessageKey = this.platform == 'omni'
            ? 'alerts.password.previousPasswordsToRememberOmni'
            : 'alerts.password.previousPasswordsToRemember';

        if (this.ent_10313_password_policy_and_mfa_global) {
            if (error?.data?.xref_error?.key === 'XREF_PASSWORD_REUSED') {
                passwordsToRemember = Number(error.data.xref_error.detail) || passwordsToRemember;
                messageKey = reuseMessageKey;
            } else {
                const policyError = this.getPasswordPolicyErrorMessage(error);
                if (policyError === `Password should be different from last ${passwordsToRemember} passwords`) {
                    messageKey = reuseMessageKey;
                } else if (policyError?.startsWith('Password')) {
                    messageKey = `alerts.password.${policyError}`;
                }
            }
        } else if (error?.message?.non_field_errors) {
            error.message.non_field_errors.forEach((item: string) => {
                if (item === `Password should be different from last ${passwordsToRemember} passwords`) {
                    messageKey = reuseMessageKey;
                }
            });
        }
        this.alertList.append([{active: true, type: 'danger', messageKey: messageKey, messageParameters: {param1: passwordsToRemember}}]);
        this.loaded = true;
    };

    private getPasswordPolicyErrorMessage(error: any): string | null {
        const message = error?.message;
        if (typeof message === 'string') {
            return message;
        }
        if (message?.non_field_errors?.length) {
            return message.non_field_errors[0];
        }
        return null;
    }

    private validationSuccess = (data: any) => {
        if (this.ent_10313_password_policy_and_mfa_global && data?.data) {
            this.getResetPasswordPolicySuccess(data);
        } else {
            this.setResetPasswordPolicyOmni();
        }
    };

    private setResetPasswordPolicyOmni() {
        this.passwordPolicy = {
            complex_password_rules: {
                force_lower_case: true,
                force_numbers: true,
                force_special_chars: true,
                force_upper_case: true
            },
            password_changes_per_day: null,
            password_minimum_length: this.ent_9105_penetration_test_2025 ? 12 : 8,
            previous_passwords_to_remember: 6,
            inactive_account_suspension_trigger: null,
            password_expiry: null,
            session_age: null
        };
        this.loaded = true;
        this.passwordPolicyLoaded = true;
        this.el.focusById('newPassword');
    }
}
